import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import DateFnsUtils from "@date-io/date-fns";

import { useAuth } from "../../contexts/auth";

import api, { CancelToken } from "../../services/api";

import GloboLogo from "../../assets/globo-logo.png";
import MediaproLogo from "../../assets/adchecking-mediapro.svg";
import CustomField from "../../components/CustomField";
import DefaultButton from "../../components/DefaultButton";
import Dialog from "../../components/Dialog";
import useGetIframeSrc from "./components/Player/hooks/useGetIframeSrc";

import { Grid, TextField, Typography } from "@material-ui/core";
import {
  getInitialTime,
  getTimeDifferenceBetweenDates,
  isTimeDifferenceGreaterThanXMinutes,
  isTimeObjectValueEqual,
  mixDates,
  validateDateAndTime,
  validateFutureDate,
} from "../../utils/date-time";
import { initialUserData } from "./mock";

import * as S from "./styles";

import { LocationAndChannel, UserData } from "./types";

import { Autocomplete } from "@material-ui/lab";
import TimePicker from "../../components/TimerPicker";
import { useDialog } from "../../contexts/dialog";
import { isGloboCurrentApp } from "../../utils/get-app-env";
import PlayerLayout from "../layout/player-layout";
import Player from "./components/Player/player";
import AutocompleteCustomField from "../../components/AutocompleteCustomField";

const PlayerPage = () => {
  const Auth = useAuth();
  const mediaRef = useRef<any>();
  const IframeRef = useRef<HTMLIFrameElement | null>(null);

  const [userData, setUserData] = useState<UserData>(initialUserData);
  const [playerErrors, setPlayerErrors] = useState<Record<string, string>>({});
  const [isFullWidth, setIsFullWidth] = useState(false);
  const [userLocation, setUserLocation] = useState<any>([]);
  const [userChannels, setUserChannels] = useState([]);
  const [locationValue, setLocationValue] = useState<LocationAndChannel>(null);
  const [locationValueArray, setLocationValueArray] = useState<
    LocationAndChannel[]
  >([]);
  const [locationInputValue, setLocationInputValue] = useState("");

  const [userChannel, setUserChannel] = useState<any>([]);

  const [channelValue, setChannelValue] = useState<LocationAndChannel>(null);
  const [channelInputValue, setChannelInputValue] = useState("");

  const mockedData = useMemo(() => getInitialTime(), []);

  const [inputStartTime, setInputStartTime] = useState<Date | null | any>(
    mockedData
  );

  const [inputEndTime, setInputEndTime] = useState<Date | null | any>(
    new Date(inputStartTime?.getTime() + 10 * 60000)
  );

  const [inputDateAndTime, setInputDateAndTime] = useState<Date | null | any>(
    mockedData
  );

  const { timeStamp, validateTimeStamp } = getTimeDifferenceBetweenDates(
    inputEndTime,
    inputStartTime
  );

  const [isTimeValid] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [iframeSrc, setIframeSrc]: any = useState();
  const [isLoadingIframe, setIsLoadingIframe] = useState(false);
  const [isMultiple, setIsMultiple] = useState(true);

  const dialog = useDialog();

  const finalMixedDate = mixDates(inputDateAndTime, inputStartTime);

  const { srcs, handleGetSrc, isLoading, resetSrcs } = useGetIframeSrc({
    finalMixedDate,
    timeStamp,
    videoGroupData: {
      location: { name: locationValue?.name },
      channel: { name: channelValue?.name },
    },
  });

  useEffect(() => {
    if (iframeSrc) {
      mediaRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  }, [isLoadingIframe, iframeSrc]);

  useEffect(() => {
    if (IframeRef.current) {
      IframeRef.current.src = ""; // Limpa o iframe ANTES de carregar um novo src
    }
  }, [locationValueArray]);

  const handleValidateTimeObjectAndStopSubmitFlowIfPass = () => {
    // Declarações
    const isTimeEqualOrLessOneSecond = isTimeObjectValueEqual(
      inputStartTime,
      inputEndTime
    );
    const isTimeGreaterThan30Minutes = isTimeDifferenceGreaterThanXMinutes(
      inputStartTime,
      inputEndTime,
      30
    );

    // Validações
    if (isTimeEqualOrLessOneSecond) {
      dialog.setDialogStatus(true);
      dialog.setDialogText(
        "Não é possível pesquisar vídeos com menos de 1 segundo de duração."
      );
      return true;
    }

    if (validateFutureDate(inputDateAndTime)) {
      dialog.setDialogStatus(true);
      dialog.setDialogText(
        "Ups, parece que está procurando por um trecho no futuro. Por favor, conferir novamente data e hora."
      );
      return true;
    }

    if (isTimeGreaterThan30Minutes) {
      dialog.setDialogStatus(true);
      dialog.setDialogText(
        "Não é possível pesquisar vídeos com mais de 30 minutos de duração."
      );
      return true;
    }

    const { dateValidation } = validateDateAndTime(inputDateAndTime);

    // setIsLoadingIframe(true);

    if (dateValidation && !validateTimeStamp) {
      dialog.setDialogStatus(true);
      dialog.setDialogText(
        "Ups, parece que está procurando por um trecho no futuro. Por favor, conferir novamente data e hora."
      );
      return true;
    }

    // Se `isMultiple`, chamamos `handleGetSrc` para cada localização no `locationValueArray`
    // if (isMultiple && Array.isArray(locationValueArray)) {
    //   locationValueArray.forEach((location) => {
    //     handleGetSrc(location?.name, true); // Agora a função recebe um parâmetro
    //   });
    // } else {
    //   handleGetSrc(locationValue?.name, true); // Passa o nome da única localização
    // }

    if (isMultiple && Array.isArray(locationValueArray)) {
      locationValueArray.forEach((location) => {
        handleGetSrc(location?.name, true).catch((error) => {
          setPlayerErrors((prev) => ({
            ...prev,
            [location?.name || ""]:
              error?.message ||
              `Erro ao carregar o vídeo para ${location?.label}`,
          }));
          dialog.setDialogStatus(true);
          dialog.setDialogText(
            `Ups, ocorreu um erro ao carregar o vídeo para ${location?.label}.`
          );
        });
      });
    } else {
      handleGetSrc(locationValue?.name, true).catch(() => {
        dialog.setDialogStatus(true);
        dialog.setDialogText(
          `Ups, ocorreu um erro ao carregar o vídeo para ${locationValue?.label}.`
        );
      });
    }

    setIsLoadingIframe(false);
    setIframeSrc(null);
    if (IframeRef.current) IframeRef.current.src = "";
    return false;
  };

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    setIsLoadingIframe(true);

    const shouldStop = handleValidateTimeObjectAndStopSubmitFlowIfPass();

    if (shouldStop) {
      setIsLoadingIframe(false);
      return;
    }

    // Verifica se há apenas 1 praça selecionada
    const isSingleLocation =
      Array.isArray(locationValueArray) && locationValueArray.length === 1;

    // Define se o player deve ser full-width
    setIsFullWidth(isSingleLocation);

    setTimeout(() => {
      setIsLoadingIframe(false);
    }, 3000);
  };

  const handleDateChange = (date: Date | null) => {
    const selectedDate = date?.getDate();
    const selectedMonth = date?.getMonth();

    inputEndTime.setDate(selectedDate);
    inputEndTime.setMonth(selectedMonth);

    inputStartTime.setDate(selectedDate);
    inputStartTime.setMonth(selectedMonth);

    setInputDateAndTime(date);
  };

  const memoizeValidateInput = useCallback(() => {
    if (
      (isMultiple && locationValueArray.length < 1) || // Verifica se `isMultiple` está ativo e `locationValueArray` está vazio
      (!isMultiple && !locationValue) || // Se `isMultiple` for falso, então `locationValue` precisa ser válido
      !channelValue ||
      !inputEndTime ||
      !inputDateAndTime ||
      inputDateAndTime?.toString().includes("Invalid Date") ||
      !isTimeValid
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [
    isMultiple,
    locationValueArray,
    locationValue,
    channelValue,
    inputEndTime,
    inputDateAndTime,
    isTimeValid,
  ]);

  useEffect(() => {
    memoizeValidateInput();
  }, [memoizeValidateInput]);

  useEffect(() => {
    const handleGetUserByUsername = async () => {
      const StoredUserData = Auth.sessionData;

      try {
        const response = await api.get(`/users/${StoredUserData?.username}`);
        setUserData(response.data);
      } catch (err) {
        console.error(err);
        Auth.logout();
      }
    };
    handleGetUserByUsername();
  }, [Auth]);

  const handleGetPracas = async (locationName: string) => {
    const source = CancelToken.source();

    if (!locationName) return;

    try {
      const dataToBeUsedAsParams = userData.groups.join(",");
      const { data } = await api.get<
        Array<{ id: number; name: string; label: string }>
      >(
        `/channels/${locationName}/locations?clipping_sources=${dataToBeUsedAsParams}`,
        {
          cancelToken: source.token,
        }
      );

      let sortedChannels = data.sort((prev, next) =>
        prev.name > next.name ? 1 : -1
      );
      setUserLocation(sortedChannels);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const handleGetChannels = async () => {
      const dataToBeUsedAsParams = userData.groups.join(",");

      try {
        const { data } = await api.get(
          `/channels?clipping_sources=${dataToBeUsedAsParams}`
        );

        if (isGloboCurrentApp()) {
          const globoChannel = data.find(
            (channel: { label: string }) => channel.label === "Globo"
          );
          setChannelValue(globoChannel || data[0]);
          setUserChannels(data);
        } else {
          setChannelValue(data[0]);
          setUserChannel(data);
        }
      } catch (err) {
        console.error(err);
      }
    };

    handleGetChannels();
  }, [userData]);

  useEffect(() => {
    const fetchData = async () => {
      if (!channelValue) return;

      await handleGetPracas(channelValue?.name);
      setLocationValue(null);
      setLocationInputValue("");
    };

    fetchData();
  }, [channelValue]);

  useEffect(() => {
    if (!channelInputValue) {
      setLocationValue(null);
      setLocationInputValue("");
      setUserLocation([]);
    }
  }, [channelInputValue]);

  const handleConvertNameAndLabel = useMemo(
    () => (options: any) => {
      return options.map((item: any) => ({
        ...item,
        nameLabel: `${item.name} - ${item.label}`,
      }));
    },
    []
  );

  const shouldRenderGloboContent = () => {
    if (!isGloboCurrentApp()) {
      return (
        <S.StyledHeaderContent>
          <S.GloboIcon src={MediaproLogo} alt="mediapro logo" />
        </S.StyledHeaderContent>
      );
    }

    return (
      <S.StyledHeaderContent>
        <S.GloboIcon src={GloboLogo} alt="globo logo" />
        <Typography variant="h6">
          Sistema de captura de conteúdo para uso exclusivo dos canais e áreas
          da Globo
        </Typography>
      </S.StyledHeaderContent>
    );
  };

  const handleRenderGloboAutocomplete = () => {
    if (isGloboCurrentApp()) {
      return (
        <CustomField
          options={userChannels}
          inputLabel="Escolher canal"
          userValue={channelValue}
          setUserValue={setChannelValue}
          textValue={channelInputValue}
          setTextValue={setChannelInputValue}
          onSelect={async (props: any) => {
            //always get new channels when location changes
            await handleGetPracas(props?.name);

            setLocationValue(null);
            setLocationInputValue("");
          }}
        />
      );
    }

    return (
      <div style={{ width: "90%" }}>
        <Autocomplete
          style={{ width: "100%" }}
          disablePortal
          value={channelValue}
          id="combo-box-demo"
          options={userChannel}
          noOptionsText="Escolher o canal"
          onChange={(event: React.ChangeEvent<{}>, newValue: any) => {
            setChannelValue(newValue);
          }}
          getOptionSelected={(option: any, value: any) => {
            return (
              option?.label === value?.label || option?.name === value?.name
            );
          }}
          getOptionLabel={(option: any) => {
            return option?.label;
          }}
          renderOption={(option: any) => (
            <React.Fragment>
              {isGloboCurrentApp() ? (
                <span>{option?.label}</span>
              ) : (
                <span className="mp-autocomplete-fragment">
                  {option?.label}
                </span>
              )}
            </React.Fragment>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Escolher o canal"
            />
          )}
        />
      </div>
    );
  };

  const renderPlayers = () => {
    if (Array.isArray(locationValueArray) && locationValueArray.length > 1) {
      return (
        <Grid
          container
          direction="row"
          justifyContent="space-evenly"
          alignItems="center"
          spacing={1}
        >
          {locationValueArray
            .filter((location) => location?.name)
            .map((location) => {
              const src = location?.name ? srcs[location.name] || "" : "";
              const hasError = !!playerErrors[location?.name || ""];
              const errorMsg =
                playerErrors[location?.label ? srcs[location.label] || "" : ""];
              const is404 = errorMsg?.includes(
                "Request failed with status code"
              );

              if ((!src && !isLoading) || is404) return null;

              return (
                <Grid
                  item
                  key={location?.name}
                  lg={locationValueArray.length > 1 ? 6 : 12}
                  md={12}
                  sm={12}
                  xs={12}
                >
                  <Player
                    src={src}
                    IframeRef={IframeRef}
                    inputStartTime={inputStartTime}
                    inputDateAndTime={inputDateAndTime}
                    finalMixedDate={mixDates(inputDateAndTime, inputStartTime)}
                    timeStamp={timeStamp}
                    location={{
                      name: location?.name,
                      label: location?.label,
                    }}
                    channel={{
                      name: channelValue?.name,
                      label: channelValue?.label,
                      inputValue: channelInputValue,
                    }}
                  />
                </Grid>
              );
            })}
        </Grid>
      );
    } else {
      return (
        <>
          <Grid item xs={12} sm={1}></Grid>
          <Grid item xs={12} sm={10}>
            <Player
              src={
                locationValueArray[0]?.name
                  ? srcs[locationValueArray[0].name] || ""
                  : ""
              }
              IframeRef={IframeRef}
              inputStartTime={inputStartTime}
              inputDateAndTime={inputDateAndTime}
              finalMixedDate={mixDates(inputDateAndTime, inputStartTime)}
              timeStamp={timeStamp}
              location={{
                name: locationValueArray[0]?.name,
                label: locationValueArray[0]?.label,
              }}
              channel={{
                name: channelValue?.name,
                label: channelValue?.label,
                inputValue: channelInputValue,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={1}></Grid>
        </>
      );
    }
  };

  const resetPlayerState = () => {
    setIframeSrc(null);
    if (IframeRef.current) IframeRef.current.src = "";

    setLocationValue(null);
    setLocationValueArray([]);
    setLocationInputValue("");

    setInputStartTime(mockedData);
    setInputEndTime(new Date(mockedData?.getTime() + 10 * 60000));
    setInputDateAndTime(mockedData);

    setPlayerErrors({});
    setIsDisabled(false);
    setIsFullWidth(false);
    setIsLoadingIframe(false);
    setIsMultiple(true);

    resetSrcs();
  };

  return (
    <>
      <PlayerLayout>
        <>
          {shouldRenderGloboContent()}
          <S.StyledForm onSubmit={handleSubmit}>
            {handleRenderGloboAutocomplete()}

            {isMultiple ? (
              <AutocompleteCustomField
                options={handleConvertNameAndLabel(userLocation)}
                inputLabel="Escolher até 2 praças"
                userValue={locationValueArray}
                setUserValue={setLocationValueArray}
                textValue={locationInputValue}
                setTextValue={setLocationInputValue}
                userRole={userData?.role}
                noOptionsText="Escolher até 2 praças"
                onSelect={() => {
                  console.log(
                    "entrou no on select do autoconolete",
                    locationValueArray,
                    locationInputValue
                  );
                  resetPlayerState();
                }}
              />
            ) : (
              <CustomField
                options={handleConvertNameAndLabel(userLocation)}
                inputLabel="Escolher a praça2"
                userValue={locationValue}
                setUserValue={setLocationValue}
                textValue={locationInputValue}
                setTextValue={setLocationInputValue}
                userRole={userData?.role}
                noOptionsText="Escolher antes um canal"
              />
            )}

            <S.StyledPickersContainer utils={DateFnsUtils}>
              <S.StyledDatePicker
                id="date-picker-dialog"
                label="Escolher a data"
                invalidDateMessage="Data inválida"
                inputVariant="outlined"
                margin="normal"
                format="dd/MM/yyyy"
                value={inputDateAndTime}
                onChange={handleDateChange}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
                disableFuture
                maxDateMessage="Data inválida"
              />
              <TimePicker
                startDate={inputStartTime}
                handleStartDate={setInputStartTime}
                endDate={inputEndTime}
                handleEndDate={setInputEndTime}
              />
            </S.StyledPickersContainer>

            <DefaultButton
              typecolor="blueButton"
              variant="contained"
              text="PESQUISAR"
              type="submit"
              disableRipple
              disabled={isDisabled || isLoading}
              styles={{ width: "90%" }}
            />
          </S.StyledForm>
        </>
        <>
          <Grid
            container
            direction="row"
            justifyContent="space-evenly"
            alignItems="center"
            spacing={1}
            style={{ height: "calc(width * 1.75)" }}
          >
            {isLoading ? (
              <S.StyledCircularProgress className="circular" size="120px" />
            ) : (
              renderPlayers()
            )}
          </Grid>
        </>
      </PlayerLayout>
      <Dialog
        openDialog={dialog.dialogStatus}
        setOpenDialog={dialog.setDialogStatus}
        dialogText={dialog?.dialogText}
        handleCloseLoading={setIsLoadingIframe}
        handleClearIframeSrc={setIframeSrc}
      />
    </>
  );
};

export default PlayerPage;
